import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  getTravellerGlobalSearchQueryKey,
  useTravellerGlobalSearchHook,
} from '@/api/trafikportalen';
import {
  getUniqueCardSerials,
  normalizeCardSerial,
} from '@/helpers/cardSerial';
import { getSchoolCardSerials } from '@/helpers/travellerSearch';

export const useSchoolCardSerials = (serials: (string | undefined)[]) => {
  const searchTravellers = useTravellerGlobalSearchHook();
  const serialsKey = serials
    .map((serial) => (serial ? normalizeCardSerial(serial) : ''))
    .filter(Boolean)
    .sort()
    .join('|');
  const uniqueSerials = useMemo(
    () => getUniqueCardSerials(serialsKey ? serialsKey.split('|') : []),
    [serialsKey],
  );

  const searches = useQueries({
    queries: uniqueSerials.map((serial) => ({
      queryKey: getTravellerGlobalSearchQueryKey({ q: serial }),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        searchTravellers({ q: serial }, signal),
      enabled: uniqueSerials.length > 0,
    })),
  });

  const schoolCardSerialsKey = getSchoolCardSerials(
    searches.flatMap((search) => search.data ?? []),
  ).join('|');
  const schoolCardSerials = useMemo(
    () =>
      new Set(
        schoolCardSerialsKey.length > 0 ? schoolCardSerialsKey.split('|') : [],
      ),
    [schoolCardSerialsKey],
  );

  return {
    isPending:
      uniqueSerials.length > 0 &&
      searches.some((search) => search.isPending || search.isFetching),
    schoolCardSerials,
  };
};
